import { FC } from 'react';
import { styled } from '@mui/material/styles';
import DoctorIcon from 'src/assets/doctor.svg';
import EmailIcon from 'src/assets/email.svg';
import FaxIcon from 'src/assets/fax.svg';
import InternetIcon from 'src/assets/internet.svg';
import PersonIcon from 'src/assets/person.svg';
import { FieldList } from 'src/components/field_list';
import { useWorkflowType } from 'src/distribution_platforms/features/hooks/use_workflow';
import { LdtOrderDetailFieldsFragment } from 'src/types';
import { WorkflowType } from 'src/types/workflows';
import { formatPhoneOrFaxNumber } from 'src/utils/phone_number_utils';

const IconImage = styled('img')({
  width: 20,
  height: 20,
});

/**
 * Ordering Provider Section Component
 *
 * Displays ordering provider information in a single column layout using CompletedForm.
 */
export const OrderingProviderSection: FC<{
  order: LdtOrderDetailFieldsFragment;
}> = ({ order }) => {
  const orderingProvider = order.orderingProvider;
  const orderingPhysician = orderingProvider?.orderingPhysician;
  const workflowType = useWorkflowType();

  // Format name from firstName and lastName
  const formatName = () => {
    if (!orderingPhysician) return null;
    const parts = [
      orderingPhysician.firstName,
      orderingPhysician.lastName,
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(' ') : null;
  };

  const fields = [
    {
      label: 'Name',
      value: formatName(),
      icon: <IconImage src={PersonIcon} alt='Person' />,
    },
    {
      label: 'NPI',
      value: orderingPhysician?.npi,
      icon: <IconImage src={DoctorIcon} alt='Doctor' />,
    },
    ...(workflowType === WorkflowType.ANCILLARY_LAB
      ? [
          {
            label: 'Clinic / Hospital Name',
            value: orderingProvider?.siteName,
            icon: <IconImage src={InternetIcon} alt='Internet' />,
          },
        ]
      : []),
    {
      label: 'Fax',
      value: formatPhoneOrFaxNumber(
        orderingProvider?.siteFaxNumber,
        orderingProvider?.siteFaxRegionCode,
      ),
      icon: <IconImage src={FaxIcon} alt='Fax' />,
    },
    {
      label: 'Email Address',
      value: orderingPhysician?.email,
      icon: <IconImage src={EmailIcon} alt='Email' />,
    },
  ];

  return <FieldList fields={fields} />;
};
